import { colors } from '@lmc/common/constants'
import { Button, Icon } from '@lmc/ui-kit'
import type React from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import styled from 'styled-components'
import { darken, lighten } from '../../../LEGACY/colorUtils'

interface Medication {
  id: string
  name: string
  doseageInstructions: string
  additionalInformation?: string
  timing: string[]
  controlledDrug?: boolean
}

interface MedicationItemProps {
  medication: Medication
  onDelete: (id: string) => void
  onEdit: (medication: Medication) => void
}

const isDesktop = isBrowser || isTablet

const MedicationItem: React.FC<MedicationItemProps> = ({
  medication,
  onDelete,
  onEdit
}) => {
  if (!medication) return null

  const {
    name,
    doseageInstructions,
    additionalInformation,
    timing,
    controlledDrug
  } = medication

  return (
    <div className="lmc-card">
      <ResponsiveContainer>
        <TitleContainer>
          <Title>{name}</Title>
          {controlledDrug && isDesktop && <Badge>Controlled Drug</Badge>}
        </TitleContainer>
        <ButtonsContainer $hasControlledDrug={controlledDrug}>
          {controlledDrug && !isDesktop && <Badge>Controlled Drug</Badge>}
          <div>
            <Button onClick={() => onEdit(medication)}>
              <Icon name="EditOutlined" />
              {isDesktop ? 'Edit' : ''}
            </Button>
            <Space />
            <Button onClick={() => onDelete(medication.id)} variant="danger">
              <Icon name="DeleteOutlined" />
              {isDesktop ? 'Delete' : ''}
            </Button>
          </div>
        </ButtonsContainer>
      </ResponsiveContainer>
      <Divider />
      <ResponsiveContainer>
        <InfoContainer>
          <InfoLabel>Dosage Instructions</InfoLabel>
          <InfoValue>{doseageInstructions}</InfoValue>
        </InfoContainer>
        <InfoContainer>
          <InfoLabel>Additional Information</InfoLabel>
          {additionalInformation ? (
            <InfoValue>{additionalInformation}</InfoValue>
          ) : (
            <InfoValue $noInformation>No information entered</InfoValue>
          )}
        </InfoContainer>
        <InfoContainer $narrowWidth>
          <InfoLabel>Timing</InfoLabel>
          <InfoValue>{timing ? timing.join(', ') : ''}</InfoValue>
        </InfoContainer>
      </ResponsiveContainer>
    </div>
  )
}

const Badge = styled.div`
  align-self: center;
  background: ${lighten(colors.red, 80)};
  color: ${darken(colors.red, 20)};
  flex-shrink: 0;
  font-size: 12px;
  font-weight: bold;
  margin-right: 8px;
  padding: 3px 12px 2px;
  border-radius: 15px;
`

const ButtonsContainer = styled.div<{ $hasControlledDrug?: boolean }>`
  display: flex;
  flex-shrink: 0;
  justify-content: ${
    isDesktop
      ? 'normal'
      : (props) => (props.$hasControlledDrug ? 'space-between' : 'flex-end')
  };
  margin-top: ${isDesktop ? '0' : '9px'};
`

const Divider = styled.div`
  background: ${colors.grey};
  height: 1px;
  margin-top: 9px;
  width: 100%;
`

const InfoContainer = styled.div<{ $narrowWidth?: boolean }>`
  padding-top: 12px;
  margin-right: 24px;
  width: ${(props) =>
    props.$narrowWidth && isDesktop ? '30%' : isDesktop ? '35%' : 'auto'};
`

const InfoLabel = styled.span`
  font-weight: bold;
`

const InfoValue = styled.div<{ $noInformation?: boolean }>`
  padding-top: 9px;
  color: ${(props) => (props.$noInformation ? colors.black : 'inherit')};
`

const Title = styled.span`
  font-size: 18px;
  font-weight: bold;
  margin-right: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: ${isDesktop ? 'nowrap' : 'normal'};
`

const TitleContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: ${isDesktop ? 'flex-start' : 'space-between'};
  min-width: 0;
`

const ResponsiveContainer = styled.div`
  display: ${isDesktop ? 'flex' : 'block'};
  justify-content: ${isDesktop ? 'space-between' : 'normal'};
`

const Space = styled.span`
  width: 5px;
  display: inline-block;
`

export default MedicationItem
